'use client';

import { useState, useEffect } from 'react';
import PriceTableRow from './PriceTableRow';
import PriceTableMobileCard from './PriceTableMobileCard';
import { useLanguage } from '@/contexts/LanguageContext';
import { priceService, Price } from '@/lib/services/priceService';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

const PriceListInteractive = () => {
  const { t, language } = useLanguage();
  const [prices, setPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPrices();

    // Set up real-time subscription
    const supabase = createClient();
    const channel = supabase
      .channel('prices-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'prices'
        },
        (payload) => {
          console.log('Real-time price update received:', payload);
          // Reload prices when any change occurs
          loadPrices();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadPrices = async () => {
    try {
      setLoading(true);
      const data = await priceService.getAll();
      setPrices(data);
      setError('');
    } catch (err: any) {
      setError(err?.message || 'Failed to load prices');
    } finally {
      setLoading(false);
    }
  };

  // Get last updated date from most recent price update
  const lastUpdatedDate = prices?.length > 0 ?
  new Date(Math.max(...prices.map((p) => new Date(p.updated_at).getTime()))).
  toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) :
  new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Separate gold and silver products
  const goldPrices = prices?.filter((item) => item.category === 'gold');
  const silverPrices = prices?.filter((item) => item.category === 'silver');

  // Transform Price to PriceItem format for existing components
  const transformPrices = (priceList: Price[]) => priceList?.map((price) => {
    // Translate product name based on category
    const translatedProductName = price.category === 'gold' ? t('priceList.goldBars')
      : t('priceList.silverBars');
    
    return {
      id: price.id,
      product: translatedProductName,
      weight: price.weight,
      purity: price.purity,
      ankauf: `${price.currency}${price.ankauf_netto.toFixed(2).replace('.', ',')}`,
      verkaufNetto: `${price.currency}${(price.verkauf_netto ?? 0).toFixed(2).replace('.', ',')}`,
      verkaufBrutto: `${price.currency}${(price.verkauf_brutto ?? 0).toFixed(2).replace('.', ',')}`,
      category: price.category
    };
  });

  const goldData = transformPrices(goldPrices);
  const silverData = transformPrices(silverPrices);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="font-body text-muted-foreground">Loading prices...</p>
      </div>);

  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg font-body text-sm">
        {error}
      </div>);

  }

  const renderTable = (data: any[], categoryLabel: string) =>
  <>
      {/* Section Heading */}
      <div className="mb-6">
        <h2 className="font-heading text-3xl font-bold text-foreground">{categoryLabel}</h2>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl shadow-luxury-md overflow-hidden border border-border/50">
        <table className="w-full">
          <thead className="bg-secondary">
            <tr>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.product')}
              </th>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.weight')}
              </th>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.purity')}
              </th>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.ankauf')}
              </th>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.verkaufNetto')}
              </th>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.verkaufBrutto')}
              </th>
              <th className="px-8 py-5 text-left font-heading text-base font-semibold text-foreground">
                {t('priceList.table.contact')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.map((item) =>
          <PriceTableRow key={item.id} item={item} />
          )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-5">
        {data?.map((item) =>
      <PriceTableMobileCard key={item.id} item={item} />
      )}
      </div>

      {/* Empty State */}
      {data?.length === 0 &&
    <div className="text-center py-12">
          <p className="font-body text-muted-foreground">No prices available for {categoryLabel}.</p>
        </div>
    }
    </>;


  return (
    <div className="relative">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
        <Image
          src="/assets/images/62471786424b2a6a3df4855f9bbbff86_1_-1769788495173.png"
          alt="Gold and silver bars"
          width={360}
          height={240}
          className="object-contain opacity-50"
        />
      </div>

      {/* Last Updated Date - Centered */}
      <div className="relative z-10 mb-8 text-center">
        <p className="font-body text-lg text-muted-foreground">
          {t('priceList.lastUpdated')}: <span className="font-semibold text-foreground">{lastUpdatedDate}</span>
        </p>
      </div>

      {/* Gold Section */}
      <div className="relative z-10 mb-12">
        {renderTable(goldData, t('priceList.goldBars'))}
      </div>

      {/* Silver Section */}
      <div className="relative z-10">
        {renderTable(silverData, t('priceList.silverBars'))}
      </div>

      {/* Contact Note */}
      <div className="mt-10 bg-accent/5 border border-accent/20 rounded-2xl p-8 text-center">
        <p className="font-body text-base text-muted-foreground leading-relaxed">
          {language === 'de' ? (
            <>
              Für Bestellungen kontaktieren Sie uns bitte per Telefon unter{' '}
              <a href="tel:+4963598676" className="text-accent hover:underline transition-luxury">+49 (0) 6359 86762</a> /{' '}
              <a href="tel:+491722973022" className="text-accent hover:underline transition-luxury">+49 (0) 172 297 30 22</a>, per Email an{' '}
              <a href="mailto:info@golddreams.eu" className="text-accent hover:underline transition-luxury">info@golddreams.eu</a> oder über das Kontaktformular.
            </>
          ) : (
            <>
              W celu złożenia zamówienia prosimy o kontakt telefoniczny pod numerem{' '}
              <a href="tel:+4963598676" className="text-accent hover:underline transition-luxury">+49 (0) 6359 86762</a> /{' '}
              <a href="tel:+491722973022" className="text-accent hover:underline transition-luxury">+49 (0) 172 297 30 22</a>, e-mailem na{' '}
              <a href="mailto:info@golddreams.eu" className="text-accent hover:underline transition-luxury">info@golddreams.eu</a> lub za pomocą formularza kontaktowego.
            </>
          )}
        </p>
      </div>
    </div>);

};

export default PriceListInteractive;